import _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { URL_CALLCENTER_PORTAL } from '@/shared/config';
import { zeroPad } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import { checkStatusStripChange } from '@/widgets/strips-management/ui/handler';
import {
  selectChangeStripData,
  selectIsLoadingStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { DeviceEditStripBoxProps } from './deviceEditStripBox';

/**
 * Backs the device strip detail box, merging the selected strip with its pending change and exposing per-field edit handlers. Reads the selected strip, change list and loading flag from the report store, resolves the effective strip data, mirrors it into local form state whenever the selection changes, keeps unsaved drafts in a ref, and clears validation errors on selection or tab change.
 *
 * @param props - Box props with the tab store key and whether the tab is currently active.
 * @returns An object carrying the loading flag, the resolved strip data, the form state, the event metadata, the disabled flags and the navigate, change, key-down, apply and cancel handlers.
 */
const useDeviceEditStripBox = ({
  keyRecoil,
  isActive,
}: Pick<DeviceEditStripBoxProps, 'keyRecoil' | 'isActive'>) => {
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const changeStripData = useReportStore(selectChangeStripData(keyRecoil));
  const setChangeStripDataAction = useReportStore((s) => s.actions.setChangeStripData);
  /**
   * Writes the pending strip change list for this tab into the report store. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - New change list, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setChangeStripData = (v: Parameters<typeof setChangeStripDataAction>[1]) =>
    setChangeStripDataAction(keyRecoil, v);
  const unSaveInfo = useRef<any[]>([]);
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));

  const [state, setState] = useMergeState<{
    technicianComment: string;
    title: string;
    titleError: string;
    commentError: string;
    isRemove: boolean;
    type: string;
    isDeviceEvent: boolean;
    isRemoved?: boolean;
    id?: any;
    eventId?: any;
    eventType?: any;
    triggerTime?: any;
    symptoms?: any;
    isOtherEvent?: any;
  }>({
    technicianComment: '',
    title: '',
    titleError: '',
    commentError: '',
    isRemove: false,
    type: '',
    isDeviceEvent: false,
  });

  const { eventId, eventType, triggerTime, symptoms } = state || {};

  const selectedData: any = useMemo(() => {
    const findChange = _.find(changeStripData, (item) => item.id === selectedStrip?.id);
    if (findChange) return { ...findChange, isChanged: true };
    // selectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark; originalInfo
    // is only on the local strip shapes built in StripsManagementV2/handler.ts - pre-existing gap.
    return selectedStrip
      ? { ...selectedStrip, isRemoved: false, ...(selectedStrip as any).originalInfo }
      : {};
  }, [selectedStrip, changeStripData]);

  const isNoEcgData = selectedData?.isNoEcgData;
  const isDisabled = selectedData?.isRemoved || _.isEmpty(selectedData) || isNoEcgData;

  /**
   * Opens the selected strip's event in the call centre portal. Builds the events URL from the portal base and the strip id and opens it in a new browser tab.
   *
   * @returns Nothing.
   */
  const onClickNavigate = () => {
    const url = `${URL_CALLCENTER_PORTAL}events/${selectedStrip.id}`;
    window.open(url, '_blank');
  };

  /**
   * Records an edit to the selected strip's title or technician comment. Writes the new value into local state while clearing that field's validation error, and mirrors it into the unsaved-info ref so the draft survives switching strips.
   *
   * @param name - Field being edited, either title or technicianComment.
   * @param value - New value for that field.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    const updateData = { [name]: value };
    if (name === 'title') {
      _.assign(updateData, { titleError: '' });
    }
    if (name === 'technicianComment') {
      _.assign(updateData, { commentError: '' });
    }
    setState(updateData);
    const savedData = _.find(unSaveInfo.current, (item) => item.id === selectedData.id);
    if (savedData) {
      _.assign(savedData, {
        [name]: value,
      });
    } else {
      unSaveInfo.current.push({
        id: selectedStrip.id,
        [name]: value,
      });
    }
  };

  /**
   * Keeps typing inside the strip box from reaching the tab's global keyboard shortcuts. Stops propagation of the key event.
   *
   * @param e - Keyboard event raised by the input.
   * @returns Nothing.
   */
  const onKeyDownInput = (e: any) => {
    e.stopPropagation();
  };

  /**
   * Drops the unsaved draft held for one field of the selected strip. Clones the unsaved-info ref, clears the title or the technician comment on the entry matching the selected strip, and writes the clone back.
   *
   * @param name - Field to clear, either title or the technician comment.
   * @returns Nothing.
   */
  const resetSaveData = (name: any) => {
    const cloneSavedData = _.cloneDeep(unSaveInfo.current);
    const savedData = _.find(cloneSavedData, (item) => item.id === selectedData.id);
    if (savedData) {
      if (name === 'title') {
        savedData.title = undefined;
      } else {
        savedData.technicianComment = undefined;
      }
    }
    unSaveInfo.current = cloneSavedData;
  };

  /**
   * Applies a title or technician comment edit to the report's pending strip changes. Rejects a blank value with a field error, otherwise copies the value onto a clone of the resolved strip, folds it into the change list through checkStatusStripChange, stores the result and clears the unsaved draft.
   *
   * @param name - Field being applied, either title or the technician comment.
   * @returns Nothing.
   */
  const onClickApplyChange = (name: any) => {
    const cloneSelectedStrip = { ...selectedData };
    if (name === 'title') {
      if (!state.title?.trim()) {
        setState({ titleError: 'Please enter a title' });
        return;
      }
      cloneSelectedStrip.title = state.title;
    } else {
      if (!state.technicianComment?.trim()) {
        setState({ commentError: 'Please enter a comment' });
        return;
      }
      cloneSelectedStrip.comment = state.technicianComment;
    }
    const changesData = checkStatusStripChange([cloneSelectedStrip], changeStripData);
    setChangeStripData(changesData);
    resetSaveData(name);
  };

  /**
   * Reverts an in-progress title or comment edit to the value stored on the strip. Clears that field's unsaved draft, then restores the value from the resolved strip data and clears its validation error.
   *
   * @param name - Field being cancelled, either title or the technician comment.
   * @returns Nothing.
   */
  const onClickCancelChange = (name: any) => {
    resetSaveData(name);
    if (name === 'title') {
      setState({ title: selectedData.title, titleError: '' });
      return;
    }
    setState({ technicianComment: selectedData.comment, commentError: '' });
  };

  useEffect(() => {
    if (!_.isEmpty(selectedData)) {
      const {
        comment,
        title,
        type,
        friendlyId,
        isDeviceEvent,
        triggerTime,
        typeText,
        symptoms,
        isOtherEvent,
        id,
        isRemoved,
        isChanged,
        originalInfo,
      } = selectedData || {};
      const updateData = {
        title: isChanged ? title : originalInfo?.title,
        technicianComment: isChanged ? comment : originalInfo?.comment,
        id,
      };
      const savedData = _.find(unSaveInfo.current, (item) => item.id === id);
      if (savedData) {
        updateData.title = !_.isNil(savedData.title) ? savedData.title : updateData.title;
        updateData.technicianComment = !_.isNil(savedData.technicianComment)
          ? savedData.technicianComment
          : updateData.technicianComment;
      }

      setState({
        isRemoved,
        ...updateData,
        type,
        eventId: zeroPad(friendlyId),
        eventType: typeText,
        triggerTime,
        symptoms,
        isDeviceEvent,
        isOtherEvent,
      });
    }
  }, [selectedData]);

  useEffect(() => {
    setState({
      titleError: '',
      commentError: '',
    });
  }, [selectedData.id, isActive]);

  return {
    isLoadingStrip,
    selectedData,
    state,
    eventId,
    eventType,
    triggerTime,
    symptoms,
    isNoEcgData,
    isDisabled,
    onClickNavigate,
    onChange,
    onKeyDownInput,
    onClickApplyChange,
    onClickCancelChange,
  };
};

export default useDeviceEditStripBox;
